import getConfig from 'next/config';
import React from 'react';

import { useCurrentLanguage } from '../../../api/hooks/useCurrentLanguage.hook';
import { useFullVariantByIdSsr } from '../../../api/hooks/useFullVariantByIdSsr.hook';
import { useInstanceDetailPage } from '../../../api/hooks/useInstanceDetail.hook';
import { useInstanceMetaByUrlSsr } from '../../../api/hooks/useInstanceMetaByUrlSsr.hook';
import { useKeywords } from '../../../api/hooks/useKeywords.hook';
import { mapLookupToLanguageHook } from '../../../api/hooks/useLookupTableData.hook';
import { useTypoContent } from '../../../api/hooks/useTypoContent.hook';
import { useVariantDetailPage } from '../../../api/hooks/useVariantDetailPage.hook';
import { VariantImage } from '../../../api/models/variant-image';
import { VariantImageType } from '../../../api/models/variant-image-type';
import { TypoService } from '../../../api/services/typo.service';
import creatoki from '../../../assets/creatoki.png';
import { RootState, useAppSelector } from '../../../redux';
import { AspectRatio } from '../../../utils/_enums/aspect-ratio.enum';
import { BasicCardVariants } from '../../../utils/_enums/basic-card-variants.enum';
import { MediaContentType } from '../../../utils/_enums/image-content-type.enum';
import { TypographyVariantsEnum } from '../../../utils/_enums/typeography-variants.enum';
import { creatokenPosterUrlResolver } from '../../../utils/_helper/creatoken-posterUrl-resolver';
import { generateUrlToUserOan } from '../../../utils/_helper/generate-url-to-user-oan';
import truncateString from '../../../utils/_helper/truncate-string-helper';
import useFilterAttachments from '../../../utils/_hooks/useFilterAttachments.hook';
import CarouselComponent from '../../_shared/carousel/carousel';
import { Media } from '../../_shared/media/media';
import { StageImageButtonToken } from '../../_shared/stage/stage-image-button-token/stage-image-button-token';
import StageImageBackground from '../../_shared/stage-image-background/stage-image-background';
import ToolTip from '../../_shared/tool-tip/tool-tip';
import { Typography } from '../../_shared/typography/typography';
import styles from './instance-detail-stage.module.scss';
import { InstanceDetailStageMetaBox } from './instance-detail-stage-meta-box/instance-detail-stage-meta-box';


export const InstanceDetailStage: React.FC & { dataHooks: any } = ({ onButtonClick }) => {
    const { instance } = useAppSelector((state: RootState) => (state.productSlice));

    const variant = useFullVariantByIdSsr();
    const instanceMeta = useInstanceMetaByUrlSsr();
    const typoService = new TypoService(useTypoContent());

    const allowDimensionalCard = typoService.getAllowedDimensionalCardProductOwners();

    const { owner } = useInstanceDetailPage();

    const language = useCurrentLanguage();

    const { data: keywords } = useKeywords();
    const { publicRuntimeConfig } = getConfig();

    const resolvedKeywords = keywords?.data
        ? mapLookupToLanguageHook(keywords?.data)
        : [];

    const getContentTypeByFileExtension = (filename: string): MediaContentType => {
        const fileExtension = filename?.split('.').pop();

        switch (fileExtension) {
            case 'jpg':
                return MediaContentType.JPG;
            case 'jpeg':
                return MediaContentType.JPEG;
            case 'mp4':
                return MediaContentType.MP4;
            case 'gif':
                return MediaContentType.GIF;
            case 'png':
                return MediaContentType.PNG;
            default:
                return MediaContentType.JPG;
        }
    };


    let covers: VariantImage[] = [];
    covers.push({
        name: instanceMeta.name,
        url: instanceMeta.image,
        variantId: variant.id,
        contentContentType: getContentTypeByFileExtension(instanceMeta.image),
        type: VariantImageType.COVER_ARTWORK
    });

    const dbCovers = useFilterAttachments(VariantImageType.COVER_ARTWORK, variant?.attachments);
    dbCovers.shift();

    covers = [...covers, ...dbCovers];

    let ownerHref: string;

    if (instance && instance.ownerId) {
        ownerHref = generateUrlToUserOan(instance?.owner?.username as string);
    }

    return (
        <div className={styles['instance-detail-top-wrapper']}>
            <div className={styles['instance-detail-stage']}>
                <div className={styles['instance-detail-stage-media-wrapper']}>
                    {
                        covers && covers.length > 0
                            ? covers[0].contentContentType !== MediaContentType.MP4
                                ? <StageImageBackground
                                    url={covers[0].url}
                                    alt={covers[0].name}
                                    objectPosition={'center'}
                                    className={styles['stage-background']}
                                />
                                : <video
                                    autoPlay
                                    loop
                                    muted
                                    className={styles['stage-background']}
                                    poster={creatokenPosterUrlResolver(variant.id ?? -1)}
                                >
                                    <source src={covers[0].url} type='video/mp4'/>
                                </video>
                            : undefined
                    }
                </div>
                <div className={styles['instance-detail-stage-content-box']}>
                    <CarouselComponent
                        type={BasicCardVariants.VARIANT_DETAIL}
                        className={styles['instance-detail-stage__carousel']}
                        items={
                            covers?.map?.(({ id, name, urls, contentContentType, url }) => (
                                <div
                                    key={id}
                                    className={`${styles[`basic-card-image-wrapper-${AspectRatio.WIDESCREEN}`]}`}>
                                    <div>
                                        <Media
                                            contentType={contentContentType}
                                            alt={name}
                                            objectFit={'contain'}
                                            src={urls?.original || url}
                                            posterUrl={creatokenPosterUrlResolver(variant.id ?? -1)}
                                            squared
                                            initialLoad={true}
                                            dimensionalCard={allowDimensionalCard.includes(variant?.product?.publisherNames.trim())}
                                        />
                                    </div>
                                </div>
                            ))
                        }
                    />
                    <InstanceDetailStageMetaBox
                        genre={''}
                        ownerWallet={instance?.ownerWallet}
                        ownerUsername={instance?.ownerUsername}
                        keywords={
                            variant && variant.keywords ? variant.keywords.map((keyword) => {
                                const res = resolvedKeywords?.find(
                                    (word) => keyword === word.id
                                );
                                if (!res) return '';

                                if (language === 'de') return res.trans_de;
                                else return res.trans_en;
                            }) : ([] as string[])
                        }
                        title={instanceMeta?.name ?? ''}
                        author={instanceMeta?.attributes.find(attr => attr.trait_type === 'Author')?.value ?? ''}
                        publisher={variant?.product?.publisherNames ?? ''}
                        release={
                            instance && instance.saleStartDate
                                ? new Date(instance.saleStartDate)
                                : variant && variant.salesAttribute && variant.salesAttribute.saleFrom ? new Date(variant.salesAttribute.saleFrom)
                                    : undefined
                        }
                        copies={
                            instance?.serialNumber &&
                            variant?.salesAttribute.mintingCount
                                ? `${instance?.serialNumber ?? 0} / ${
                                    variant?.salesAttribute.mintingCount ?? 0
                                }`
                                : ''
                        }
                        onButtonClick={onButtonClick}
                    />

                </div>
            </div>
            <div>
                <div className={`${styles.decButtonContainer}`}>
                    {
                        instance && instance.ownerId
                            ? <StageImageButtonToken
                                shadow
                                image={`${publicRuntimeConfig.backendURL}api/user-profile/image/${instance.ownerId}`}
                                hasLink
                                href={ownerHref}
                            />
                            : <StageImageButtonToken
                                shadow
                                image={creatoki.src}
                            />
                    }
                </div>
                <div className={styles['owner-info']}>
                    {instance &&
                    (instance.ownerUsername || instance.ownerWallet) ? (
                        <div className={styles['wallet-address-wrapper']}>
                            <Typography
                                variant={TypographyVariantsEnum.TABLE_TITLE}
                            >
                                {owner}
                            </Typography>
                            <ToolTip
                                toolTip={instance.ownerUsername ?? instance.ownerWallet}
                            >
                                <Typography
                                    variant={TypographyVariantsEnum.TABLE_VALUE}
                                    element={'span'}
                                >
                                    {instance.ownerUsername
                                        ? truncateString(instance.ownerUsername, 4, 4)
                                        : truncateString(instance.ownerWallet, 4, 4)}
                                </Typography>
                            </ToolTip>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

InstanceDetailStage.dataHooks = [useInstanceDetailPage, useVariantDetailPage, useFullVariantByIdSsr, useInstanceMetaByUrlSsr];
